/**
 * Fee invoices raised against a merchant.
 *
 * The record itself is core's `BusinessInvoice`, which the server persists, so
 * this file does not restate it. `AdminBusinessInvoice` only narrows the shape
 * to what JSON does to it (every Date arrives as an ISO string, and the
 * response whitelist strips the server-side `logs`) and adds the admin-only
 * request types around it. It is named `Admin...` rather than `BusinessInvoice`
 * so it does not shadow core's record in admin's namespace.
 */

/**
 * Core owns the statuses. Re-exported rather than redeclared so admin and the
 * server cannot drift, and so the duplicate-name check has one less collision
 * to report.
 */
export {
  BusinessInvoiceStatus,
  BusinessInvoiceType,
} from '@afriex/core/domain/enums';

import {
  BusinessInvoiceStatus,
  type BusinessInvoiceType,
} from '@afriex/core/domain/enums';
import type {
  BusinessInvoice as CoreBusinessInvoice,
  BusinessInvoiceMeta,
  BusinessInvoiceUser,
} from '@afriex/core/domain/types';

export type {
  BusinessInvoiceAmount,
  BusinessInvoiceLogEntry,
  BusinessInvoiceMeta,
  BusinessInvoiceTimestamp,
  BusinessInvoiceUser,
  InvoiceSettlementAccountSnapshot,
} from '@afriex/core/domain/types';

/**
 * What the endpoints actually return: the core invoice with every Date
 * serialized. Derived rather than redeclared, so a field added to the record
 * cannot go missing here; only the date representation differs.
 */
export type AdminBusinessInvoice = Omit<
  CoreBusinessInvoice,
  'dates' | 'createdAt' | 'updatedAt' | 'meta'
> & {
  id: string;
  dates: {
    periodStart: string;
    periodEnd: string;
    dueDate: string;
  };
  /** Written on create and preserved by every update; `logs` never leaves the server. */
  meta: Omit<BusinessInvoiceMeta, 'logs'>;
  createdAt: string;
  updatedAt: string;
};

export type BusinessInvoiceListParams = {
  page?: number;
  limit?: number;
  businessId?: string;
  status?: BusinessInvoiceStatus;
  reference?: string;
  fromDate?: string | Date;
  toDate?: string | Date;
};

/**
 * What an invoice for a business and period bills back from overdrafts, as the
 * create form's amount fetcher receives it.
 */
export type BusinessOverdraftTotal = {
  amount: number;
  currency: string;
  /** How many draws the total covers. */
  count: number;
  /** Draws left out of the total: no published rate into `currency`. */
  unconverted?: number;
};

export type CreateBusinessInvoicePayload = {
  /** Who the invoice is billed to; the fees screen picks a business. */
  toUser: BusinessInvoiceUser;
  /** Who issued it; the server defaults this to the calling admin. */
  fromUser?: BusinessInvoiceUser;
  /**
   * Set by the form rather than chosen: the fees screen raises nothing but fee
   * invoices. Sent explicitly so the server never has to infer it from the
   * route.
   */
  invoiceType: BusinessInvoiceType;
  amount: number;
  currency: string;
  periodStart: string;
  periodEnd: string;
  dueDate: string;
  settlementAccountId: string;
  description?: string;
};

/** Recording what happened to an invoice after it was raised. */
export type UpdateBusinessInvoiceStatusPayload = {
  status: BusinessInvoiceStatus;
  /** Required when the new status is PartiallyPaid: how much was actually paid. */
  amountPaid?: number;
  note?: string;
};

/**
 * Invoice status to Badge colorScheme. Covers every value core defines, so a
 * status the server sends can never render an unstyled badge.
 */
export const invoiceStatusColors: Record<BusinessInvoiceStatus, string> = {
  [BusinessInvoiceStatus.Draft]: 'gray',
  [BusinessInvoiceStatus.Sent]: 'blue',
  [BusinessInvoiceStatus.Viewed]: 'indigo',
  [BusinessInvoiceStatus.PartiallyPaid]: 'refunded',
  [BusinessInvoiceStatus.Paid]: 'successful',
  [BusinessInvoiceStatus.Overdue]: 'failed',
  [BusinessInvoiceStatus.Cancelled]: 'gray',
};
